import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Plus, Download, RefreshCw, Search, Trash2, ChevronRight, ChevronDown,
  Briefcase, IndianRupee, Calendar, Building2, FileText, Wallet, BarChart3, ChevronLeft,
  CreditCard, Settings2,
} from "lucide-react";

const EVENT_TYPES = [
  { key: "wedding",          label: "Wedding" },
  { key: "private_social",   label: "Private Social" },
  { key: "leisure",          label: "Leisure" },
  { key: "corporate_outing", label: "Corporate Outing" },
  { key: "day_outing",       label: "Day Outing" },
];

const PAYMENT_MODES = [
  { key: "upi",            label: "UPI" },
  { key: "cash",           label: "Cash" },
  { key: "bank_transfer",  label: "Bank Transfer" },
  { key: "razor_pay",      label: "RazorPay" },
  { key: "cheque",         label: "Cheque" },
];

const PURPOSE_TAGS = ["Advance", "Booking", "Partial", "Balance", "Final", "Refund", "Other"];

const PERIOD_LABEL = {
  weekly: "This Week",
  monthly: "This Month",
  quarterly: "This Quarter",
  half_yearly: "Half-Yearly",
  yearly: "This Year",
};

const fmtINR = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

async function downloadBlob(url, fallbackName, mime) {
  const resp = await api.get(url, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: mime });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export default function Turnover() {
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [bookingsByClient, setBookingsByClient] = useState({});
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedClientId, setExpandedClientId] = useState(null);

  // Dialog state
  const [clientDialog, setClientDialog] = useState({ open: false, edit: null });
  const [bookingDialog, setBookingDialog] = useState({ open: false, clientId: null, edit: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, booking: null });
  const [projectionDialog, setProjectionDialog] = useState({ open: false, clientId: null, edit: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [projectionsByClient, setProjectionsByClient] = useState({});
  const [projectionsSummary, setProjectionsSummary] = useState(null); // {type, id, label}

  // Reports state
  const [reportsSummary, setReportsSummary] = useState([]);
  const [reportDialog, setReportDialog] = useState({ open: false, period: "monthly", anchor: null });

  // Configurable payment modes
  const [paymentModes, setPaymentModes] = useState(PAYMENT_MODES);
  const [manageModesOpen, setManageModesOpen] = useState(false);

  // Per-client payment breakup + filtered export
  const [clientPaymentsDialog, setClientPaymentsDialog] = useState({ open: false, client: null });
  const [exportDialog, setExportDialog] = useState(false);
  const paymentModeLabel = useMemo(
    () => Object.fromEntries((paymentModes || []).map((m) => [m.key, m.label])),
    [paymentModes],
  );

  const loadModes = useCallback(async () => {
    try {
      const { data } = await api.get("/turnover/payment-modes");
      if (Array.isArray(data) && data.length) setPaymentModes(data);
    } catch (_e) { /* keep fallback defaults */ }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    // Load resiliently: a turnover user may not have the `staff` permission (used
    // by /users) or other module perms. An auxiliary 403 must NOT blank out the
    // core turnover data. Anyone with the module permission sees all the data.
    const results = await Promise.allSettled([
      api.get("/turnover/summary"),
      api.get("/turnover/clients"),
      api.get("/properties"),
      api.get("/users"),
      api.get("/turnover/reports/summary"),
      api.get("/turnover/projections/summary"),
      api.get("/turnover/payment-modes"),
    ]);
    const val = (i, fallback) => (results[i].status === "fulfilled" ? results[i].value.data : fallback);
    setSummary(val(0, null));
    setClients(val(1, []) || []);
    setProperties(val(2, []) || []);
    setUsers(val(3, []) || []);
    setReportsSummary(val(4, []) || []);
    setProjectionsSummary(val(5, null));
    const modes = val(6, null);
    if (Array.isArray(modes) && modes.length) setPaymentModes(modes);
    // Only surface an error if the core turnover endpoints themselves failed.
    const coreFailed = results[0].status === "rejected" || results[1].status === "rejected";
    if (coreFailed) {
      const reason = (results[1].reason || results[0].reason)?.response?.data?.detail;
      toast.error(reason || "Failed to load turnover data");
    }
    setLoading(false);
  }, []);

  const loadProjectionsFor = useCallback(async (clientId) => {
    try {
      const { data } = await api.get(`/turnover/projections?client_id=${clientId}`);
      setProjectionsByClient((m) => ({ ...m, [clientId]: data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load projections");
    }
  }, []);

  const loadBookingsFor = useCallback(async (clientId) => {
    try {
      const { data } = await api.get(`/turnover/bookings?client_id=${clientId}`);
      setBookingsByClient((m) => ({ ...m, [clientId]: data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load bookings");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const expandClient = (cid) => {
    if (expandedClientId === cid) {
      setExpandedClientId(null);
      return;
    }
    setExpandedClientId(cid);
    if (!bookingsByClient[cid]) loadBookingsFor(cid);
    if (!projectionsByClient[cid]) loadProjectionsFor(cid);
  };

  const convertProjection = async (pid, clientId) => {
    try {
      await api.post(`/turnover/projections/${pid}/convert`);
      toast.success("Converted to booking");
      await loadAll();
      await loadBookingsFor(clientId);
      await loadProjectionsFor(clientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    }
  };

  const deleteProjection = async (pid, clientId) => {
    if (!window.confirm("Delete this projection?")) return;
    try {
      await api.delete(`/turnover/projections/${pid}`);
      toast.success("Removed");
      await loadAll();
      await loadProjectionsFor(clientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.primary_contact, c.phone, c.email].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [clients, search]);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/turnover/export", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `turnover_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(href);
      toast.success("Exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const confirmAndDelete = async () => {
    if (!confirmDelete) return;
    const { type, id, clientId } = confirmDelete;
    try {
      if (type === "client") await api.delete(`/turnover/clients/${id}`);
      else if (type === "booking") await api.delete(`/turnover/bookings/${id}`);
      else if (type === "payment") await api.delete(`/turnover/payments/${id}`);
      toast.success("Removed");
      setConfirmDelete(null);
      await loadAll();
      if (clientId) await loadBookingsFor(clientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-5 max-w-6xl" data-testid="turnover-page">
      <div>
        <span className="label-overline flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> Corporate Office</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Turnover</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Track every client, their bookings across properties, and the payments collected against each booking.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total Billed"   value={fmtINR(summary?.billed_total)}   icon={IndianRupee} />
        <Stat label="Collected"      value={fmtINR(summary?.paid_total)}     icon={Wallet} accent="emerald" />
        <Stat label="Outstanding"    value={fmtINR(summary?.outstanding)}    icon={IndianRupee} accent={summary?.outstanding > 0 ? "amber" : "neutral"} />
        <Stat label="Active Bookings" value={summary?.active_bookings ?? 0} icon={Calendar} />
      </div>

      {/* Projections dashboard card */}
      {projectionsSummary && (
        <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="turnover-projections-section">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <h2 className="font-display text-lg font-bold tracking-tight">Sales projections</h2>
            <span className="text-[11px] text-zinc-400">upcoming payments expected from clients</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Open projections" value={projectionsSummary.open_count} icon={Briefcase} />
            <Stat label="Projected (open)" value={fmtINR(projectionsSummary.open_total)} icon={IndianRupee} accent="amber" />
            <Stat label="Overdue" value={`${projectionsSummary.overdue_count} · ${fmtINR(projectionsSummary.overdue_total)}`} icon={IndianRupee} accent={projectionsSummary.overdue_count > 0 ? "amber" : "neutral"} />
            <Stat label="Converted" value={`${projectionsSummary.converted_count} · ${fmtINR(projectionsSummary.converted_total)}`} icon={Wallet} accent="emerald" />
          </div>
          {projectionsSummary.upcoming && projectionsSummary.upcoming.length > 0 && (
            <div>
              <div className="label-overline mb-2">Upcoming projections</div>
              <ul className="space-y-1.5">
                {projectionsSummary.upcoming.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-sm bg-zinc-50 rounded px-3 py-2">
                    <div className="min-w-0">
                      <span className="font-semibold">{p.client_name}</span>
                      <span className="text-[11px] text-zinc-500 ml-2 capitalize">{(p.event_type || "").replace(/_/g, " ")}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-mono text-zinc-600">{p.projected_payment_date}</span>
                      <span className="font-bold tabular-nums">{fmtINR(p.projected_amount)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Period reports */}
      <section data-testid="turnover-reports-section">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-zinc-500" />
          <h2 className="font-display text-lg font-bold tracking-tight">Period reports</h2>
          <span className="text-[11px] text-zinc-400">click a card to drill down</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {reportsSummary.map((r) => (
            <button
              key={r.period}
              type="button"
              onClick={() => setReportDialog({ open: true, period: r.period, anchor: r.start_date })}
              className="text-left bg-white border border-zinc-200 rounded-md p-3 hover:border-zinc-950 hover:shadow-sm transition-all group"
              data-testid={`report-card-${r.period}`}
            >
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                <span>{PERIOD_LABEL[r.period] || r.period}</span>
                <ChevronRight className="w-3 h-3 opacity-40 group-hover:opacity-100" />
              </div>
              <div className="text-xs text-zinc-600 truncate">{r.label.split("·")[0].trim()}</div>
              <div className="text-2xl font-bold font-display mt-1 tabular-nums">{fmtINR(r.collected)}</div>
              <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-x-3">
                <span>{r.payments_count} payments</span>
                <span>{r.unique_clients} client{r.unique_clients === 1 ? "" : "s"}</span>
                {r.bookings_count > 0 && <span>{r.bookings_count} bookings</span>}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-400" />
          <Input
            placeholder="Search client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 w-64"
            data-testid="turnover-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={loadAll} className="h-9" data-testid="turnover-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={downloadXlsx} disabled={downloading} className="h-9" data-testid="turnover-export">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Excel
          </Button>
          <Button variant="outline" onClick={() => setExportDialog(true)} className="h-9" data-testid="turnover-export-filtered">
            <FileText className="w-4 h-4 mr-1" /> Export (filtered)
          </Button>
          <Button variant="outline" onClick={() => setManageModesOpen(true)} className="h-9" data-testid="turnover-manage-modes">
            <CreditCard className="w-4 h-4 mr-1" /> Payment modes
          </Button>
          <Button onClick={() => setClientDialog({ open: true, edit: null })} className="h-9 bg-zinc-950 hover:bg-zinc-800" data-testid="turnover-add-client">
            <Plus className="w-4 h-4 mr-1" /> New client
          </Button>
        </div>
      </div>

      {/* Clients accordion */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        {filteredClients.length === 0 && (
          <div className="py-12 text-center text-zinc-400">
            <Briefcase className="w-5 h-5 mx-auto mb-2" /> No clients yet — start by adding one.
          </div>
        )}
        {filteredClients.map((c) => {
          const expanded = expandedClientId === c.id;
          const bookings = bookingsByClient[c.id] || [];
          return (
            <div key={c.id} className="border-b border-zinc-100 last:border-0" data-testid={`turnover-client-${c.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3 px-3 md:px-4 py-3 hover:bg-zinc-50">
                <button onClick={() => expandClient(c.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`turnover-client-toggle-${c.id}`}>
                  {expanded ? <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3">
                      {c.primary_contact && <span>{c.primary_contact}</span>}
                      {c.phone && <span className="font-mono">{c.phone}</span>}
                      {c.email && <span className="truncate max-w-[60vw]">{c.email}</span>}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-6 text-xs md:ml-auto md:pl-2">
                  <Mini label="Bookings"     value={c.booking_count} />
                  <Mini label="Billed"       value={fmtINR(c.billed_total)} />
                  <Mini label="Collected"    value={fmtINR(c.paid_total)} accent="emerald" />
                  <Mini label="Outstanding"  value={fmtINR(c.outstanding)} accent={c.outstanding > 0 ? "amber" : "neutral"} />
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setClientPaymentsDialog({ open: true, client: c })} data-testid={`turnover-view-payments-${c.id}`}>
                    <Wallet className="w-3 h-3 mr-1" /> Payments
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setBookingDialog({ open: true, clientId: c.id, edit: null })} data-testid={`turnover-add-booking-${c.id}`}>
                    <Plus className="w-3 h-3 mr-1" /> Booking
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setProjectionDialog({ open: true, clientId: c.id, edit: null })} data-testid={`turnover-add-projection-${c.id}`}>
                    <Plus className="w-3 h-3 mr-1" /> Projection
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setClientDialog({ open: true, edit: c })} data-testid={`turnover-edit-client-${c.id}`}>
                    <FileText className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmDelete({ type: "client", id: c.id, label: c.name })} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`turnover-delete-client-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {expanded && (
                <div className="bg-zinc-50 border-t border-zinc-100 px-3 md:px-6 py-3 space-y-4">
                  <div>
                    <div className="label-overline mb-2">Bookings</div>
                    {bookings.length === 0 ? (
                      <div className="text-xs text-zinc-400 italic py-2">No bookings yet for this client.</div>
                    ) : (
                      <div className="space-y-2">
                        {bookings.map((b) => (
                          <BookingRow
                            key={b.id}
                            booking={b}
                            properties={properties}
                            onEdit={() => setBookingDialog({ open: true, clientId: c.id, edit: b })}
                            onDelete={() => setConfirmDelete({ type: "booking", id: b.id, clientId: c.id, label: `${EVENT_TYPES.find(e => e.key === b.event_type)?.label || b.event_type} at ${b.property_name}` })}
                            onAddPayment={() => setPaymentDialog({ open: true, booking: b })}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {(projectionsByClient[c.id] || []).length > 0 && (
                    <div>
                      <div className="label-overline mb-2">Projections</div>
                      <div className="space-y-2">
                        {(projectionsByClient[c.id] || []).map((p) => (
                          <div key={p.id} className={`bg-white border ${p.status === "converted" ? "border-emerald-200" : "border-zinc-200"} rounded-md p-3`} data-testid={`projection-row-${p.id}`}>
                            <div className="flex flex-col md:flex-row md:items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest bg-zinc-100 text-zinc-700">
                                    {EVENT_TYPES.find((e) => e.key === p.event_type)?.label || p.event_type}
                                  </span>
                                  <span className="text-sm font-semibold">{p.property_name}</span>
                                  <span className="text-xs text-zinc-500 font-mono">{p.check_in_date} → {p.check_out_date}</span>
                                  {p.status === "converted" && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">Converted</span>
                                  )}
                                </div>
                                <div className="text-[11px] text-zinc-500 mt-0.5">
                                  Payment expected by <span className="font-mono font-semibold text-zinc-700">{p.projected_payment_date}</span> · <span className="font-bold tabular-nums">{fmtINR(p.projected_amount)}</span>
                                </div>
                                {p.notes && <div className="text-[11px] text-zinc-500 mt-1 italic">{p.notes}</div>}
                              </div>
                              <div className="flex gap-1.5 shrink-0">
                                {p.status === "open" && (
                                  <Button size="sm" onClick={() => convertProjection(p.id, c.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`projection-convert-${p.id}`}>
                                    Convert to booking
                                  </Button>
                                )}
                                {p.status === "open" && (
                                  <Button size="sm" variant="outline" onClick={() => setProjectionDialog({ open: true, clientId: c.id, edit: p })} data-testid={`projection-edit-${p.id}`}>
                                    <FileText className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => deleteProjection(p.id, c.id)} className="text-red-600 border-red-200" data-testid={`projection-delete-${p.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialogs */}
      <ClientDialog
        open={clientDialog.open}
        edit={clientDialog.edit}
        onOpenChange={(v) => setClientDialog({ open: v, edit: clientDialog.edit })}
        onSaved={() => loadAll()}
      />
      <BookingDialog
        open={bookingDialog.open}
        clientId={bookingDialog.clientId}
        edit={bookingDialog.edit}
        properties={properties}
        onOpenChange={(v) => setBookingDialog({ ...bookingDialog, open: v })}
        onSaved={async () => {
          await loadAll();
          if (bookingDialog.clientId) await loadBookingsFor(bookingDialog.clientId);
        }}
      />
      <PaymentDialog
        open={paymentDialog.open}
        booking={paymentDialog.booking}
        users={users}
        paymentModes={paymentModes}
        onOpenChange={(v) => setPaymentDialog({ ...paymentDialog, open: v })}
        onChanged={async () => {
          await loadAll();
          if (paymentDialog.booking) await loadBookingsFor(paymentDialog.booking.client_id);
        }}
      />
      <ProjectionDialog
        open={projectionDialog.open}
        clientId={projectionDialog.clientId}
        edit={projectionDialog.edit}
        properties={properties}
        onOpenChange={(v) => setProjectionDialog({ ...projectionDialog, open: v })}
        onSaved={async () => {
          await loadAll();
          const cid = projectionDialog.clientId || projectionDialog.edit?.client_id;
          if (cid) {
            await loadProjectionsFor(cid);
            if (expandedClientId !== cid) setExpandedClientId(cid);
          }
        }}
      />
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {confirmDelete?.type}?</DialogTitle>
            <DialogDescription>
              <strong>{confirmDelete?.label}</strong>
              {confirmDelete?.type === "client" && " and all of its bookings and payments will be deleted."}
              {confirmDelete?.type === "booking" && " and all of its payments will be deleted."}
              {" This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button onClick={confirmAndDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="turnover-confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReportDrillDialog
        open={reportDialog.open}
        period={reportDialog.period}
        anchor={reportDialog.anchor}
        modeLabel={paymentModeLabel}
        onOpenChange={(v) => setReportDialog({ ...reportDialog, open: v })}
        onJump={(period, anchor) => setReportDialog({ open: true, period, anchor })}
      />

      <ManagePaymentModesDialog
        open={manageModesOpen}
        modes={paymentModes}
        onOpenChange={setManageModesOpen}
        onChanged={loadModes}
      />

      <ClientPaymentsDialog
        open={clientPaymentsDialog.open}
        client={clientPaymentsDialog.client}
        modeLabel={paymentModeLabel}
        onOpenChange={(v) => setClientPaymentsDialog({ ...clientPaymentsDialog, open: v })}
      />

      <ExportDialog
        open={exportDialog}
        clients={clients}
        onOpenChange={setExportDialog}
      />
    </div>
  );
}

function ManagePaymentModesDialog({ open, modes, onOpenChange, onChanged }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const name = label.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/turnover/payment-modes", { label: name });
      toast.success(`Added "${name}"`);
      setLabel("");
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add payment mode");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove payment mode "${m.label}"?`)) return;
    try {
      await api.delete(`/turnover/payment-modes/${m.key}`);
      toast.success(`Removed "${m.label}"`);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove payment mode");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manage-payment-modes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4" /> Payment modes</DialogTitle>
          <DialogDescription>Add the payment types your team uses when recording turnover payments.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-72 overflow-auto pr-1">
          {(modes || []).map((m) => (
            <div key={m.key} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid={`payment-mode-row-${m.key}`}>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-zinc-400" />
                <span className="font-medium text-sm">{m.label}</span>
                {m.is_system && <span className="text-[10px] uppercase tracking-widest text-zinc-400 bg-zinc-100 rounded px-1.5 py-0.5">Built-in</span>}
              </div>
              {!m.is_system && (
                <button onClick={() => remove(m)} className="p-1 hover:bg-red-50 hover:text-red-600 rounded text-zinc-400" title="Remove" data-testid={`payment-mode-delete-${m.key}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-zinc-200 pt-3">
          <Label className="text-xs">Add a new payment mode</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="e.g. Demand Draft, Card, NEFT"
              data-testid="payment-mode-input"
            />
            <Button onClick={add} disabled={busy || !label.trim()} className="bg-zinc-950 hover:bg-zinc-800 shrink-0" data-testid="payment-mode-add">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  const palette = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber:   "border-amber-200 bg-amber-50/40",
    neutral: "bg-white",
  };
  return (
    <div className={`border border-zinc-200 rounded-md p-3 ${palette[accent || "neutral"] || palette.neutral}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
        {Icon ? <Icon className="w-3 h-3" /> : null} {label}
      </div>
      <div className="text-xl sm:text-2xl font-bold font-display mt-1 tabular-nums truncate">{value}</div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  const cls = accent === "emerald" ? "text-emerald-700" : accent === "amber" ? "text-amber-700" : "text-zinc-900";
  return (
    <div className="hidden md:block">
      <div className="text-[9px] uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function BookingRow({ booking, properties, onEdit, onDelete, onAddPayment }) {
  const evt = EVENT_TYPES.find((e) => e.key === booking.event_type);
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-3" data-testid={`turnover-booking-${booking.id}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest bg-zinc-950 text-white">
              {evt?.label || booking.event_type}
            </span>
            <span className="text-sm font-semibold flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-zinc-400" /> {booking.property_name}</span>
            <span className="text-xs text-zinc-500 font-mono">{booking.check_in_date} → {booking.check_out_date}</span>
          </div>
          {booking.notes && <div className="text-[11px] text-zinc-500 mt-1 italic">{booking.notes}</div>}
        </div>
        <div className="flex items-center gap-6 text-xs md:ml-auto">
          <Mini label="Billed"      value={fmtINR(booking.total_billing_value)} />
          <Mini label="Collected"   value={fmtINR(booking.paid_total)} accent="emerald" />
          <Mini label="Outstanding" value={fmtINR(booking.outstanding)} accent={booking.outstanding > 0 ? "amber" : "neutral"} />
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" variant="outline" onClick={onAddPayment} data-testid={`turnover-add-payment-${booking.id}`}>
            <Plus className="w-3 h-3 mr-1" /> Payment
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`turnover-edit-booking-${booking.id}`}>
            <FileText className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`turnover-delete-booking-${booking.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ClientDialog({ open, edit, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", primary_contact: "", phone: "", email: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: edit?.name || "",
        primary_contact: edit?.primary_contact || "",
        phone: edit?.phone || "",
        email: edit?.email || "",
        notes: edit?.notes || "",
      });
    }
  }, [open, edit]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setBusy(true);
    try {
      if (edit?.id) await api.put(`/turnover/clients/${edit.id}`, form);
      else await api.post("/turnover/clients", form);
      toast.success(edit?.id ? "Updated" : "Client added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="turnover-client-dialog">
        <DialogHeader>
          <DialogTitle>{edit?.id ? "Edit client" : "New client"}</DialogTitle>
          <DialogDescription>Add a client / event organizer. Each client can have multiple bookings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Client name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="client-name" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Primary contact" value={form.primary_contact} onChange={(v) => setForm({ ...form, primary_contact: v })} />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          </div>
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="turnover-client-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {edit?.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingDialog({ open, clientId, edit, properties, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    check_in_date: "", check_out_date: "", event_type: "wedding",
    property_id: "", total_billing_value: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        check_in_date: edit?.check_in_date || "",
        check_out_date: edit?.check_out_date || "",
        event_type: edit?.event_type || "wedding",
        property_id: edit?.property_id || (properties[0]?.id || ""),
        total_billing_value: edit?.total_billing_value ?? "",
        notes: edit?.notes || "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, edit]);

  const save = async () => {
    if (!form.check_in_date || !form.check_out_date) { toast.error("Both dates are required"); return; }
    if (!form.property_id) { toast.error("Pick a property"); return; }
    const amt = parseFloat(form.total_billing_value);
    if (isNaN(amt) || amt < 0) { toast.error("Billing value must be ≥ 0"); return; }
    setBusy(true);
    try {
      const payload = { ...form, client_id: clientId || edit?.client_id, total_billing_value: amt };
      if (edit?.id) await api.put(`/turnover/bookings/${edit.id}`, payload);
      else await api.post("/turnover/bookings", payload);
      toast.success(edit?.id ? "Booking updated" : "Booking created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="turnover-booking-dialog">
        <DialogHeader>
          <DialogTitle>{edit?.id ? "Edit booking" : "New booking"}</DialogTitle>
          <DialogDescription>Capture the event details for this client.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Check-in *</Label>
              <Input type="date" value={form.check_in_date} onChange={(e) => setForm({ ...form, check_in_date: e.target.value })} data-testid="booking-checkin" />
            </div>
            <div>
              <Label className="text-xs">Check-out *</Label>
              <Input type="date" value={form.check_out_date} onChange={(e) => setForm({ ...form, check_out_date: e.target.value })} data-testid="booking-checkout" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Event type *</Label>
            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
              <SelectTrigger data-testid="booking-event-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Property *</Label>
            <Select value={form.property_id || "_none"} onValueChange={(v) => setForm({ ...form, property_id: v === "_none" ? "" : v })}>
              <SelectTrigger data-testid="booking-property"><SelectValue placeholder="Pick a property" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Total billing value (₹) *</Label>
            <Input type="number" step="0.01" min={0} value={form.total_billing_value} onChange={(e) => setForm({ ...form, total_billing_value: e.target.value })} placeholder="e.g. 450000.00" data-testid="booking-billing" />
          </div>
          <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="turnover-booking-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {edit?.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectionDialog({ open, clientId, edit, properties, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    check_in_date: "", check_out_date: "", event_type: "wedding",
    property_id: "", projected_amount: "", projected_payment_date: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        check_in_date: edit?.check_in_date || "",
        check_out_date: edit?.check_out_date || "",
        event_type: edit?.event_type || "wedding",
        property_id: edit?.property_id || (properties[0]?.id || ""),
        projected_amount: edit?.projected_amount ?? "",
        projected_payment_date: edit?.projected_payment_date || "",
        notes: edit?.notes || "",
      });
    }
  }, [open, edit]);

  const save = async () => {
    if (!form.check_in_date || !form.check_out_date) { toast.error("Both event dates are required"); return; }
    if (form.check_out_date < form.check_in_date) { toast.error("Check-out must be on or after check-in"); return; }
    if (!form.property_id) { toast.error("Pick a property"); return; }
    if (!form.projected_payment_date) { toast.error("Projected payment date is required"); return; }
    const amt = parseFloat(form.projected_amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Projected amount must be greater than 0"); return; }
    setBusy(true);
    try {
      const payload = { ...form, client_id: clientId || edit?.client_id, projected_amount: amt };
      if (edit?.id) await api.put(`/turnover/projections/${edit.id}`, payload);
      else await api.post("/turnover/projections", payload);
      toast.success(edit?.id ? "Projection updated" : "Projection added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="turnover-projection-dialog">
        <DialogHeader>
          <DialogTitle>{edit?.id ? "Edit projection" : "New projection"}</DialogTitle>
          <DialogDescription>Log an expected booking and the payment you anticipate collecting. Convert it to a real booking once confirmed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Check-in *</Label>
              <Input type="date" value={form.check_in_date} onChange={(e) => setForm({ ...form, check_in_date: e.target.value })} data-testid="projection-checkin" />
            </div>
            <div>
              <Label className="text-xs">Check-out *</Label>
              <Input type="date" value={form.check_out_date} onChange={(e) => setForm({ ...form, check_out_date: e.target.value })} data-testid="projection-checkout" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Event type *</Label>
            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
              <SelectTrigger data-testid="projection-event-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Property *</Label>
            <Select value={form.property_id || "_none"} onValueChange={(v) => setForm({ ...form, property_id: v === "_none" ? "" : v })}>
              <SelectTrigger data-testid="projection-property"><SelectValue placeholder="Pick a property" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Projected amount (₹) *</Label>
              <Input type="number" step="0.01" min={0} value={form.projected_amount} onChange={(e) => setForm({ ...form, projected_amount: e.target.value })} placeholder="e.g. 250000.00" data-testid="projection-amount" />
            </div>
            <div>
              <Label className="text-xs">Payment expected by *</Label>
              <Input type="date" value={form.projected_payment_date} onChange={(e) => setForm({ ...form, projected_payment_date: e.target.value })} data-testid="projection-payment-date" />
            </div>
          </div>
          <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="projection-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="turnover-projection-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {edit?.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, booking, users, paymentModes, onOpenChange, onChanged }) {
  const today = () => new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    amount: "", payment_mode: "upi",
    collected_by_user_id: "", collected_by_name: "",
    date_of_payment: today(), purpose: "", tags: [], reference_number: "",
  });
  const [busy, setBusy] = useState(false);
  const [payments, setPayments] = useState([]);

  const loadPayments = useCallback(async () => {
    if (!booking?.id) return;
    try {
      const { data } = await api.get(`/turnover/bookings/${booking.id}/payments`);
      setPayments(data || []);
    } catch (_e) {
      setPayments([]);
    }
  }, [booking?.id]);

  useEffect(() => {
    if (open) {
      setForm({
        amount: "", payment_mode: "upi",
        collected_by_user_id: "", collected_by_name: "",
        date_of_payment: today(), purpose: "", tags: [], reference_number: "",
      });
      loadPayments();
    }
  }, [open, loadPayments]);

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (!form.date_of_payment) { toast.error("Date is required"); return; }
    if (!form.collected_by_user_id && !form.collected_by_name.trim()) {
      toast.error("Pick a collector or type a name"); return;
    }
    setBusy(true);
    try {
      await api.post(`/turnover/bookings/${booking.id}/payments`, {
        ...form, booking_id: booking.id, amount: amt,
      });
      toast.success("Payment recorded");
      await loadPayments();
      onChanged?.();
      setForm({ ...form, amount: "", reference_number: "", purpose: "", tags: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Delete this payment entry?")) return;
    try {
      await api.delete(`/turnover/payments/${pid}`);
      toast.success("Removed");
      await loadPayments();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="turnover-payment-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-zinc-100">
          <DialogTitle>Payments — {booking?.client_name}</DialogTitle>
          <DialogDescription>
            Billed {fmtINR(booking?.total_billing_value)} · Collected {fmtINR(payments.reduce((a, p) => a + (p.amount || 0), 0))} · {booking?.event_type?.replace(/_/g, " ")} at {booking?.property_name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
        {/* Existing payments */}
        <div className="border border-zinc-200 rounded-md overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
              <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                <th className="py-2 px-2">Date</th>
                <th className="py-2 px-2 text-right">Amount</th>
                <th className="py-2 px-2">Mode</th>
                <th className="py-2 px-2">Collected by</th>
                <th className="py-2 px-2">Reference</th>
                <th className="py-2 px-2">Purpose</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-zinc-400 text-xs italic">No payments yet.</td></tr>
              )}
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 last:border-0" data-testid={`payment-row-${p.id}`}>
                  <td className="py-1.5 px-2 font-mono text-xs">{p.date_of_payment}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-bold">{fmtINR(p.amount)}</td>
                  <td className="py-1.5 px-2 text-xs capitalize">{(p.payment_mode || "").replace(/_/g, " ")}</td>
                  <td className="py-1.5 px-2 text-xs">{p.collected_by_name}</td>
                  <td className="py-1.5 px-2 text-xs font-mono text-zinc-500">{p.reference_number || "—"}</td>
                  <td className="py-1.5 px-2 text-xs">{p.purpose || ""} <span className="text-zinc-400">{(p.tags || []).join(", ")}</span></td>
                  <td className="py-1.5 px-2 text-right">
                    <button onClick={() => removePayment(p.id)} className="p-1 hover:bg-red-50 hover:text-red-600 rounded" title="Delete">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* New payment form */}
        <div className="border-t border-zinc-200 pt-4 space-y-3">
          <div className="label-overline">Add a payment</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount (₹) *</Label>
              <Input type="number" step="0.01" min={0} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="e.g. 50000.00" data-testid="payment-amount" />
            </div>
            <div>
              <Label className="text-xs">Payment mode *</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger data-testid="payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(paymentModes || PAYMENT_MODES).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date of payment *</Label>
              <Input type="date" value={form.date_of_payment} onChange={(e) => setForm({ ...form, date_of_payment: e.target.value })} data-testid="payment-date" />
            </div>
            <div>
              <Label className="text-xs">Reference / UTR / UPI ref</Label>
              <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="optional" data-testid="payment-reference" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Collected by (system user)</Label>
              <Select value={form.collected_by_user_id || "_free"} onValueChange={(v) => {
                if (v === "_free") setForm({ ...form, collected_by_user_id: "" });
                else {
                  const u = users.find((x) => x.id === v);
                  setForm({ ...form, collected_by_user_id: v, collected_by_name: u?.name || "" });
                }
              }}>
                <SelectTrigger data-testid="payment-collector-select"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_free">— Type a name instead —</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Or type a name *</Label>
              <Input value={form.collected_by_name} onChange={(e) => setForm({ ...form, collected_by_name: e.target.value })} placeholder="e.g. Front desk attendant" data-testid="payment-collector-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Purpose</Label>
            <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Booking advance for wedding" data-testid="payment-purpose" />
          </div>
          <div>
            <Label className="text-xs">Tags</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {PURPOSE_TAGS.map((t) => {
                const on = form.tags.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    className={`text-[11px] px-2 py-1 rounded border ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}
                    data-testid={`payment-tag-${t.toLowerCase()}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        </div>

        <DialogFooter className="px-6 py-4 shrink-0 border-t border-zinc-200 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="close-modal-button">Close</Button>
          <Button onClick={save} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="turnover-payment-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDrillDialog({ open, period, anchor, modeLabel, onOpenChange, onJump }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!open || !period) return;
    setLoading(true);
    try {
      const q = anchor ? `?period=${period}&anchor=${anchor}` : `?period=${period}`;
      const { data } = await api.get(`/turnover/reports/period${q}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [open, period, anchor]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="report-drill-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> {PERIOD_LABEL[period] || period} report
              </DialogTitle>
              <DialogDescription>{data?.label || (loading ? "Loading…" : "")}</DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onJump(period, data?.previous_anchor)}
                disabled={!data?.previous_anchor || loading}
                data-testid="report-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onJump(period, data?.next_anchor)}
                disabled={!data?.next_anchor || loading}
                data-testid="report-next"
              >
                Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-20 text-center text-zinc-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Period totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Collected" value={fmtINR(totals.collected)} icon={Wallet} accent="emerald" />
              <Stat label="Payments" value={totals.payments_count} icon={IndianRupee} />
              <Stat label="Bookings started" value={totals.bookings_count} icon={Calendar} />
              <Stat label="Clients" value={totals.unique_clients} icon={Briefcase} />
            </div>

            {/* By client */}
            <section>
              <h3 className="label-overline mb-2">Customer contributions ({data.by_client.length})</h3>
              {data.by_client.length === 0 ? (
                <div className="text-xs text-zinc-400 italic py-4">No payments collected in this period.</div>
              ) : (
                <div className="border border-zinc-200 rounded-md overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                        <th className="py-2 px-3">Client</th>
                        <th className="py-2 px-3">Contact</th>
                        <th className="py-2 px-3 text-right">Collected</th>
                        <th className="py-2 px-3 text-right">Payments</th>
                        <th className="py-2 px-3 text-right">Bookings paid</th>
                        <th className="py-2 px-3 text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_client.map((r) => {
                        const share = totals.collected > 0 ? (r.collected / totals.collected) * 100 : 0;
                        return (
                          <tr key={r.client_id} className="border-b border-zinc-100 last:border-0" data-testid={`report-client-${r.client_id}`}>
                            <td className="py-2 px-3 font-semibold">{r.client_name}</td>
                            <td className="py-2 px-3 text-xs text-zinc-600">
                              {r.primary_contact && <div>{r.primary_contact}</div>}
                              {r.phone && <div className="font-mono">{r.phone}</div>}
                              {r.email && <div>{r.email}</div>}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums font-bold">{fmtINR(r.collected)}</td>
                            <td className="py-2 px-3 text-right tabular-nums">{r.payments_count}</td>
                            <td className="py-2 px-3 text-right tabular-nums">{r.bookings_count}</td>
                            <td className="py-2 px-3 text-right tabular-nums text-zinc-500">{share.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* By property + by event */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <section>
                <h3 className="label-overline mb-2">By property</h3>
                {data.by_property.length === 0 ? (
                  <div className="text-xs text-zinc-400 italic">—</div>
                ) : (
                  <ul className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-100">
                    {data.by_property.map((r) => (
                      <li key={r.property_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-zinc-400" /> {r.property_name}</span>
                        <span className="tabular-nums">
                          <span className="font-bold">{fmtINR(r.collected)}</span>
                          <span className="text-[10px] text-zinc-500 ml-2">{r.bookings_count} bk</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <h3 className="label-overline mb-2">By event type</h3>
                {data.by_event_type.length === 0 ? (
                  <div className="text-xs text-zinc-400 italic">No bookings started in this period.</div>
                ) : (
                  <ul className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-100">
                    {data.by_event_type.map((r) => (
                      <li key={r.event_type} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="capitalize">{(r.event_type || "").replace(/_/g, " ")}</span>
                        <span className="tabular-nums">
                          <span className="font-bold">{fmtINR(r.billing_total)}</span>
                          <span className="text-[10px] text-zinc-500 ml-2">{r.bookings_count} bk</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {/* Payment log */}
            <section>
              <h3 className="label-overline mb-2">Payments in this period ({data.payments.length})</h3>
              {data.payments.length === 0 ? (
                <div className="text-xs text-zinc-400 italic">—</div>
              ) : (
                <div className="border border-zinc-200 rounded-md overflow-x-auto">
                  <table className="w-full text-sm min-w-[800px]">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3">Client</th>
                        <th className="py-2 px-3">Property</th>
                        <th className="py-2 px-3 text-right">Amount</th>
                        <th className="py-2 px-3">Mode</th>
                        <th className="py-2 px-3">Collected by</th>
                        <th className="py-2 px-3">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((p) => (
                        <tr key={p.id} className="border-b border-zinc-100 last:border-0">
                          <td className="py-2 px-3 font-mono text-xs">{p.date_of_payment}</td>
                          <td className="py-2 px-3">{p.client_name}</td>
                          <td className="py-2 px-3 text-xs">{p.property_name}</td>
                          <td className="py-2 px-3 text-right tabular-nums font-bold">{fmtINR(p.amount)}</td>
                          <td className="py-2 px-3 text-xs">{(modeLabel && modeLabel[p.payment_mode]) || p.payment_mode}</td>
                          <td className="py-2 px-3 text-xs">{p.collected_by_name}</td>
                          <td className="py-2 px-3 text-xs font-mono text-zinc-500">{p.reference_number || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function ClientPaymentsDialog({ open, client, modeLabel, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/turnover/clients/${client.id}/payments`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, client?.id]);

  const exportClient = async (format) => {
    if (!client?.id) return;
    setDownloading(true);
    try {
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const mime = format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const safe = (client.name || "client").replace(/[^\w]+/g, "_");
      await downloadBlob(
        `/turnover/export/custom?format=${format}&client_id=${client.id}`,
        `${safe}_payments.${ext}`, mime,
      );
      toast.success("Exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const t = data?.totals;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90dvh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="client-payments-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-zinc-100">
          <DialogTitle>Payment breakup — {client?.name}</DialogTitle>
          <DialogDescription>
            Every payment recorded by the team across all of this client's bookings.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : !data ? (
            <p className="text-sm text-zinc-400 py-8 text-center">Couldn't load payments.</p>
          ) : (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <Stat label="Collected" value={fmtINR(t.collected)} icon={Wallet} accent="emerald" />
                <Stat label="Billed" value={fmtINR(t.billed)} icon={IndianRupee} />
                <Stat label="Outstanding" value={fmtINR(t.outstanding)} icon={IndianRupee} accent={t.outstanding > 0 ? "amber" : "neutral"} />
                <Stat label="Payments" value={`${t.payments_count} · ${t.bookings_count} bkg`} icon={FileText} />
              </div>

              {/* By mode */}
              {data.by_mode.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="client-payments-by-mode">
                  {data.by_mode.map((m) => (
                    <span key={m.mode} className="text-[11px] bg-zinc-100 border border-zinc-200 rounded px-2 py-1">
                      <span className="capitalize text-zinc-600">{modeLabel?.[m.mode] || (m.mode || "").replace(/_/g, " ")}</span>
                      <span className="font-bold tabular-nums ml-1.5">{fmtINR(m.amount)}</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Per booking */}
              <div>
                <div className="label-overline mb-2">By booking</div>
                <div className="space-y-1.5">
                  {data.by_booking.map((b) => (
                    <div key={b.booking_id || "none"} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded px-3 py-1.5 text-xs" data-testid={`client-booking-subtotal-${b.booking_id || "none"}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-semibold capitalize">{(b.event_type || "—").replace(/_/g, " ")}</span>
                        <span className="text-zinc-500 truncate">{b.property_name}</span>
                        <span className="text-zinc-400 font-mono hidden sm:inline">{b.check_in_date} → {b.check_out_date}</span>
                      </div>
                      <div className="shrink-0 tabular-nums">
                        <span className="font-bold text-emerald-700">{fmtINR(b.collected)}</span>
                        <span className="text-zinc-400"> / {fmtINR(b.total_billing_value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* All payments table */}
              <div>
                <div className="label-overline mb-2">All payments ({data.payments.length})</div>
                <div className="border border-zinc-200 rounded-md overflow-x-auto">
                  <table className="w-full text-sm min-w-[760px]">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                        <th className="py-2 px-2">Date</th>
                        <th className="py-2 px-2">Event / Property</th>
                        <th className="py-2 px-2 text-right">Amount</th>
                        <th className="py-2 px-2">Mode</th>
                        <th className="py-2 px-2">Collected by</th>
                        <th className="py-2 px-2">Reference</th>
                        <th className="py-2 px-2">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.length === 0 && (
                        <tr><td colSpan={7} className="py-8 text-center text-zinc-400 text-xs italic">No payments yet for this client.</td></tr>
                      )}
                      {data.payments.map((p) => (
                        <tr key={p.id} className="border-b border-zinc-100 last:border-0" data-testid={`client-payment-row-${p.id}`}>
                          <td className="py-1.5 px-2 font-mono text-xs">{p.date_of_payment}</td>
                          <td className="py-1.5 px-2 text-xs">
                            <span className="capitalize font-medium">{(p.event_type || "—").replace(/_/g, " ")}</span>
                            <span className="text-zinc-400"> · {p.property_name}</span>
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums font-bold">{fmtINR(p.amount)}</td>
                          <td className="py-1.5 px-2 text-xs capitalize">{modeLabel?.[p.payment_mode] || (p.payment_mode || "").replace(/_/g, " ")}</td>
                          <td className="py-1.5 px-2 text-xs">{p.collected_by_name}</td>
                          <td className="py-1.5 px-2 text-xs font-mono text-zinc-500">{p.reference_number || "—"}</td>
                          <td className="py-1.5 px-2 text-xs">{p.purpose || ""} <span className="text-zinc-400">{(p.tags || []).join(", ")}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 shrink-0 border-t border-zinc-200 bg-white gap-2">
          <Button variant="outline" onClick={() => exportClient("excel")} disabled={downloading || !data} data-testid="client-payments-export-excel">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Excel
          </Button>
          <Button variant="outline" onClick={() => exportClient("pdf")} disabled={downloading || !data} data-testid="client-payments-export-pdf">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} PDF
          </Button>
          <Button onClick={() => onOpenChange(false)} className="bg-zinc-950 hover:bg-zinc-800" data-testid="client-payments-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExportDialog({ open, clients, onOpenChange }) {
  const blank = {
    client_id: "", payment_date_from: "", payment_date_to: "",
    month: "", check_in_from: "", check_in_to: "", check_out_from: "", check_out_to: "",
  };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setF(blank); /* eslint-disable-next-line */ }, [open]);

  const run = async (format) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ format });
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const mime = format === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      await downloadBlob(
        `/turnover/export/custom?${params.toString()}`,
        `turnover_payments_${new Date().toISOString().slice(0, 10)}.${ext}`, mime,
      );
      toast.success("Exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="turnover-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Download className="w-4 h-4" /> Export payments</DialogTitle>
          <DialogDescription>Filter the payment records, then download as Excel or PDF. All filters are optional — leave blank to include everything.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Client</Label>
            <Select value={f.client_id || "_all"} onValueChange={(v) => setF({ ...f, client_id: v === "_all" ? "" : v })}>
              <SelectTrigger data-testid="export-client-select"><SelectValue placeholder="All clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All clients</SelectItem>
                {(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Month (payment date)</Label>
            <Input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} data-testid="export-month" />
            <p className="text-[10px] text-zinc-400 mt-0.5">Shortcut for payment-date range. Leave blank to use the explicit range below.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Payment date from</Label>
              <Input type="date" value={f.payment_date_from} onChange={(e) => setF({ ...f, payment_date_from: e.target.value })} data-testid="export-pay-from" />
            </div>
            <div>
              <Label className="text-xs">Payment date to</Label>
              <Input type="date" value={f.payment_date_to} onChange={(e) => setF({ ...f, payment_date_to: e.target.value })} data-testid="export-pay-to" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Check-in from</Label>
              <Input type="date" value={f.check_in_from} onChange={(e) => setF({ ...f, check_in_from: e.target.value })} data-testid="export-ci-from" />
            </div>
            <div>
              <Label className="text-xs">Check-in to</Label>
              <Input type="date" value={f.check_in_to} onChange={(e) => setF({ ...f, check_in_to: e.target.value })} data-testid="export-ci-to" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Check-out from</Label>
              <Input type="date" value={f.check_out_from} onChange={(e) => setF({ ...f, check_out_from: e.target.value })} data-testid="export-co-from" />
            </div>
            <div>
              <Label className="text-xs">Check-out to</Label>
              <Input type="date" value={f.check_out_to} onChange={(e) => setF({ ...f, check_out_to: e.target.value })} data-testid="export-co-to" />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setF(blank)} disabled={busy} data-testid="export-clear">Clear</Button>
          <Button variant="outline" onClick={() => run("excel")} disabled={busy} data-testid="export-run-excel">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Excel
          </Button>
          <Button onClick={() => run("pdf")} disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" data-testid="export-run-pdf">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
